import { between, maxLength, numeric, required, requiredIf } from 'vuelidate/lib/validators';
import beamlineSpecification from '@/json/beamlineSpecification';
import { isBetween, isNoEmptyField } from '@/assets/validators/validatorHelpers';

const endStationSpecification = beamlineSpecification.endStationBSpecification;

const endStationValidator = {
  endStationParameters: {
    additional_shifts: {},
    detection_modes: { required },
    experiment_type: { required },
    photon_energy_from: {
      required,
      numeric,
      isSmallerThan() {
        return parseInt(this.endStationParameters.photon_energy_from, 10)
          <= parseInt(this.endStationParameters.photon_energy_to, 10);
      },
      between: between(
        endStationSpecification.energy_range.energy_range_from,
        endStationSpecification.energy_range.energy_range_to,
      ),
    },
    photon_energy_to: {
      required,
      numeric,
      isGreaterThan() {
        return parseInt(this.endStationParameters.photon_energy_to, 10)
          >= parseInt(this.endStationParameters.photon_energy_from, 10);
      },
      between: between(
        endStationSpecification.energy_range.energy_range_from,
        endStationSpecification.energy_range.energy_range_to,
      ),
    },
    photon_polarisation: { required },
    measurement_temperature_from: {
      required,
      numeric,
      isSmallerThan() {
        return parseInt(this.endStationParameters.measurement_temperature_from, 10)
          <= parseInt(this.endStationParameters.measurement_temperature_to, 10);
      },
      between: between(
        endStationSpecification.measurement.temperature_range.temperature_range_from,
        endStationSpecification.measurement.temperature_range.temperature_range_to,
      ),
    },
    measurement_temperature_to: {
      required,
      numeric,
      isGreaterThan() {
        return parseInt(this.endStationParameters.measurement_temperature_to, 10)
          >= parseInt(this.endStationParameters.measurement_temperature_from, 10);
      },
      between: between(
        endStationSpecification.measurement.temperature_range.temperature_range_from,
        endStationSpecification.measurement.temperature_range.temperature_range_to,
      ),
    },
    cryogenic_liquids: { required },
    lhe_use_justification: {
      requiredIf: requiredIf(function isLHeSelected() {
        if (this.endStationParameters.cryogenic_liquids) {
          return this.endStationParameters.cryogenic_liquids.includes('LHe');
        }
        return false;
      }),
      maxLength: maxLength(300),
    },
    sample_preparation: { required },
    heating: {
      requiredIf: requiredIf(function () {
        if (this.endStationParameters.sample_preparation) {
          return this.endStationParameters.sample_preparation.includes('In situ');
        }
        return false;
      }),
    },
    preparation_temperature_from: {
      requiredIf: requiredIf(function () {
        return this.endStationParameters.heating;
      }),
      numeric,
      isSmallerThan() {
        if (this.endStationParameters.heating) {
          return parseInt(this.endStationParameters.preparation_temperature_from, 10)
            <= parseInt(this.endStationParameters.preparation_temperature_to, 10);
        }
        return true;
      },
      betweenValues() {
        if (this.endStationParameters.heating) {
          return isBetween(
            parseInt(this.endStationParameters.preparation_temperature_from, 10),
            endStationSpecification.sample_preparation.temperature_range.temperature_range_from,
            endStationSpecification.sample_preparation.temperature_range.temperature_range_to,
          );
        }
        return true;
      },
    },
    preparation_temperature_to: {
      requiredIf: requiredIf(function () {
        return this.endStationParameters.heating;
      }),
      numeric,
      isGreaterThan() {
        if (this.endStationParameters.heating) {
          return parseInt(this.endStationParameters.preparation_temperature_to, 10)
            >= parseInt(this.endStationParameters.preparation_temperature_from, 10);
        }
        return true;
      },
      betweenValues() {
        if (this.endStationParameters.heating) {
          return isBetween(
            parseInt(this.endStationParameters.preparation_temperature_to, 10),
            endStationSpecification.sample_preparation.temperature_range.temperature_range_from,
            endStationSpecification.sample_preparation.temperature_range.temperature_range_to,
          );
        }
        return true;
      },
    },
    evaporation: { required },
    evaporation_data: {
      requiredIf(val) {
        if (this.endStationParameters.evaporation) return isNoEmptyField(val);
        return true;
      },
    },
    evaporator: {
      requiredIf: requiredIf(function () {
        return this.endStationParameters.evaporation;
      }),
    },
    own_evaporator: {
      requiredIf(val) {
        if (this.endStationParameters.evaporator === 'Own') {
          return isNoEmptyField(val);
        }
        return true;
      },
    },
    ar_sputtering: { required },
    gas_dosing: { required },
    gas_dosing_data: {
      requiredIf(val) {
        if (this.endStationParameters.gas_dosing) {
          return isNoEmptyField(val);
        }
        return true;
      },
    },
    detection_methods: { required },
    inserting_to_chamber: { required },
    vacuum_suitcase: {
      requiredIf: requiredIf(function () {
        if (this.endStationParameters.inserting_to_chamber) {
          return this.endStationParameters.inserting_to_chamber.includes('Vacuum suitcase');
        }
        return false;
      }),
      notEmptyField(val) {
        if (this.endStationParameters.inserting_to_chamber
          && this.endStationParameters.inserting_to_chamber.includes('Vacuum suitcase')
          && val) {
          return isNoEmptyField(val.reduce((acc, el) => acc.concat([Object.values(el)]), []));
        }
        return true;
      },

    },
  },
};

export { endStationValidator };
